import {
  Control,
  FieldValues,
  Path,
  useFieldArray,
  ArrayPath,
  useFormContext,
  useWatch,
} from "react-hook-form";
import { IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { InputField } from "@src/shared/ui/InputField";
import { useEffect } from "react";

import styles from "./EditableListField.module.scss";

interface Props<T extends FieldValues> {
  control: Control<T>;
  name: ArrayPath<T>;
  title: string;
  placeholder?: string;
  isDisabled?: boolean;
}

export const EditableListField = <T extends FieldValues>({
  control,
  name,
  title,
  placeholder = "Введите значение",
  isDisabled,
}: Props<T>) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  const { trigger } = useFormContext<T>();

  const watchedValue = useWatch({
    control,
    name: name as Path<T>,
  });

  useEffect(() => {
    trigger(name as Path<T>);
  }, [watchedValue, name, trigger]);

  const handleAddItem = () => {
    append({ value: "" } as any);
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.header}>
        <span className={styles.title}>{title}</span>

        <button
          type="button"
          className={styles.addButton}
          onClick={handleAddItem}
          disabled={isDisabled}
        >
          <AddIcon sx={{ width: 18, height: 18 }} />
          Добавить
        </button>
      </div>

      <div className={styles.list}>
        {!fields.length && (
          <button
            type="button"
            className={styles.emptyState}
            onClick={handleAddItem}
            disabled={isDisabled}
          >
            + Добавить первый пункт
          </button>
        )}

        {fields.map((field, index) => (
          <div className={styles.item} key={field.id}>
            <InputField
              control={control}
              name={`${name}.${index}.value` as Path<T>}
              className={styles.inputField}
              classNameForWrapper={styles.inputFieldWrapper}
              placeholder={placeholder}
              disabled={isDisabled}
            />
            <IconButton
              type="button"
              onClick={() => remove(index)}
              disabled={isDisabled}
              className={styles.deleteButton}
            >
              <DeleteOutlineIcon sx={{ width: 20, height: 20 }} />
            </IconButton>
          </div>
        ))}
      </div>
    </div>
  );
};
